import {
  LoadingOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  PrinterOutlined,
  HighlightOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { retryingLabel } from '../../../components/sent/failedFaxVisibility';

// `status` names the delivery state each icon stands for. The legacy Sent table only
// reads name/color/text; the redesigned table (ENG-3560) maps `status` to its chips, so
// both surfaces share one set of rules instead of re-deriving them.
const failureIcon = message => ({
  name: ExclamationCircleOutlined,
  color: '#b31c0e',
  text: message,
  status: 'failed',
});

const defaultIcon = {
  name: CheckCircleOutlined,
  color: '#037d03',
  text: 'The record was sent successfully.',
  status: 'sent',
};

const scheduledIcon = {
  name: ClockCircleOutlined,
  color: '#037d03',
  text: 'The record is scheduled to be sent.',
  status: 'scheduled',
};

const signatureRequestCompletedIcon = {
  name: HighlightOutlined,
  color: '#037d03',
  text: 'This record has been signed by the recipient.',
  status: 'signed',
};

const signatureRequestNotCompletedIcon = {
  ...signatureRequestCompletedIcon,
  color: '#fc1703',
  text: 'This record has not been signed yet.',
  status: 'unsigned',
};

const viewCountIcon = {
  ...defaultIcon,
  name: EyeOutlined,
  text: 'The record has been viewed by the recipient.',
  status: 'viewed',
};

const inProgressIcon = {
  name: LoadingOutlined,
  color: '#ffee00',
  text: 'The record is still being sent.',
  status: 'sending',
};

const directFaxSuccessIcon = {
  ...defaultIcon,
  name: PrinterOutlined,
  text: 'The record was sent as a fax',
  status: 'faxed',
};

// ENG-3427: a fax dialed from a fax machine (ATA) transmitted successfully but has no
// digital document, so the row opens an explanation instead of the PDF viewer. Say so
// on the row itself rather than letting the user find out by clicking.
const ataFaxIcon = {
  ...directFaxSuccessIcon,
  text: 'Sent from a fax machine. The document is not available digitally.',
  status: 'ata',
};

const getSentToProviderIcon = (row) => {
  const {
    view_count: viewCount,
    fax_status: faxStatus,
    record_status: recordStatus,
    is_ata_fax: isAtaFax,
  } = row;

  let icon = defaultIcon;
  const successfulDirectFax = (faxStatus === 'success' && recordStatus === 'direct_fax');
  const transmissionFailure = (faxStatus === 'failure' || faxStatus === 'partialsuccess');
  const scheduledFax = (faxStatus === 'scheduled');

  if (viewCount >= 1) icon = viewCountIcon;
  if (faxStatus === 'inprogress') {
    const retrying = retryingLabel(row);
    icon = retrying ? { ...inProgressIcon, text: `${retrying}. The first attempt did not go through, so it is being sent again automatically.` } : inProgressIcon;
  } else if (successfulDirectFax && viewCount < 1) icon = directFaxSuccessIcon;
  else if (transmissionFailure) icon = failureIcon(row.fax_error_message);
  else if (scheduledFax) icon = scheduledIcon;

  // After the chain, not inside it: a failed or in-flight ATA transmission is the more
  // useful thing to surface (the operator can redial at the machine), and the
  // view-count icon is meaningless here because there is no document for a recipient
  // to have opened.
  if (isAtaFax && !transmissionFailure && faxStatus !== 'inprogress') icon = ataFaxIcon;

  return icon;
};

const getSentToPatientIcon = (row) => {
  const {
    view_count: viewCount,
    status: smsDeliveryStatus,
  } = row;

  let icon = defaultIcon;
  const hasDeliveryFailed = ['delivery_failed', 'sending_failed'].includes(smsDeliveryStatus);
  const isSmsQueued = ['queued', 'sending', 'delivery_unconfirmed', 'sent'].includes(smsDeliveryStatus);

  if (viewCount >= 1) icon = viewCountIcon;
  if (isSmsQueued) icon = inProgressIcon;
  if (hasDeliveryFailed) icon = failureIcon('Record transmission failed');

  return icon;
};

const getSignatureCompletedIcon = (row) => {

  const { signature_request_completed: signatureRequestCompleted } = row;

  switch (signatureRequestCompleted) {
    case true:
      return signatureRequestCompletedIcon;
    case false:
      return signatureRequestNotCompletedIcon;
    default:
      return null;
  }
};

// The explanation shown for a send to a Medsender user. Same wording as the legacy
// Status button and thunderbolt tooltips in constants.js (which keep their own copy);
// used by the redesigned table's "Medsender" chip.
const MEDSENDER_USER_TOOLTIP = 'Sending a record to a Medsender user guarantees instant delivery, gives recipients the ability to search through the record, and the ability to message the sender/recipient via Medsender Connect Chat!';

export {
  MEDSENDER_USER_TOOLTIP,
  getSignatureCompletedIcon,
  getSentToPatientIcon,
  getSentToProviderIcon,
};
